import type { Contact } from '@/types/contacts'

// Consolida as duas implementações de avatarColor que existiam no arquivo original
// (uma de módulo, uma aninhada em buildConvHtml com paleta/hash diferentes) numa só.
// Efeito colateral aceito: a cor do avatar nas conversas exportadas em HTML muda
// (era gerada pela versão aninhada) — puramente cosmético, sem impacto funcional.
export function avatarColor(name: string): string {
  const colors = ['#BC0C24', '#0369A1', '#1F8C5A', '#FF6600', '#7C3AED', '#0891B2', '#B45309']
  let h = 0
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h)
  return colors[Math.abs(h) % colors.length]
}

export function initials(name: string): string {
  return (name || '?').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase()
}

export function statusLabel(s?: string): string {
  const m: Record<string, string> = { online: 'Online', busy: 'Ocupado', offline: 'Offline' }
  return m[s || ''] || 'Offline'
}

export function onlyDigits(s?: string): string {
  return (s || '').replace(/\D/g, '')
}

/** Query só com dígitos/pontuação de telefone (+, -, espaço, parênteses) e ao menos 3 dígitos. */
export function isPhoneLikeQuery(q: string): boolean {
  const t = q.trim()
  return /^[\d+\-\s().]+$/.test(t) && onlyDigits(t).length >= 3
}

/** +5521999999999 → "(21) 99999-9999". Fora do padrão BR (DDI 55 + DDD + 8/9 dígitos), devolve como veio. */
export function fmtPhone(phone?: string): string {
  if (!phone) return ''
  const digits = onlyDigits(phone)
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    const ddd  = digits.slice(2, 4)
    const rest = digits.slice(4)
    const cut  = rest.length === 9 ? 5 : 4
    return `(${ddd}) ${rest.slice(0, cut)}-${rest.slice(cut)}`
  }
  return phone
}

/** Máscara de CPF (11 dígitos) ou CNPJ (14 dígitos); fora desses tamanhos, devolve como veio. */
export function fmtDoc(doc: string): string {
  const d = onlyDigits(doc)
  if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
  return doc
}

export function fmtDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function fmtDateFull(ts: number): string {
  return new Date(ts * 1000).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function fmtDateTimeSecs(ts: number): string {
  return new Date(ts * 1000).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export function fmtChannel(ch: string): string {
  const m: Record<string, string> = {
    'Channel::Whatsapp': 'WhatsApp', 'Channel::Api': 'API',
    'Channel::Email': 'E-mail', 'Channel::WebWidget': 'Widget Web',
  }
  return m[ch] || ch.replace('Channel::', '')
}

export function convStatusLabel(s: string): string {
  const m: Record<string, string> = { open: 'Aberto', resolved: 'Resolvido', pending: 'Pendente', snoozed: 'Snoozado' }
  return m[s] || s
}

export function hasSocialProfiles(c: Contact): boolean {
  const sp = c.additional_attributes?.social_profiles
  return !!sp && Object.values(sp).some(v => !!v)
}

export function hasCustomAttrs(c: Contact): boolean {
  const ca = c.custom_attributes
  return !!ca && Object.keys(ca).length > 0
}

export function socialIcon(key: string): string {
  const icons: Record<string, string> = {
    linkedin:  '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>',
    github:    '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>',
    twitter:   '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/></svg>',
    facebook:  '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>',
    instagram: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>',
    telegram:  '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M22.26 2.05 2.19 9.92c-1.37.55-1.36 1.31-.25 1.65l5.12 1.6 11.84-7.47c.56-.34 1.07-.16.65.22L8.96 16.48l-.37 5.26c.54 0 .78-.25 1.07-.52l2.58-2.51 5.36 3.96c.99.55 1.7.27 1.95-.92l3.53-16.62c.36-1.43-.55-2.08-1.82-1.58z"/></svg>',
    tiktok:    '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.2 8.2 0 0 0 4.8 1.54V6.78a4.85 4.85 0 0 1-1.03-.09z"/></svg>',
  }
  return icons[key] || `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`
}
